<template>
  <div class="ampere-law-viz">
    <div class="viz-layout">
      <!-- 渲染舞台 -->
      <div class="viz-stage-3d" ref="canvasContainer">
        <div class="hud-overlay" v-if="initialized">
          <div class="status-badge" :class="current > 0 ? 'pos' : 'neg'">
            <label>电流 I</label>
            <span>{{ current.toFixed(1) }} A {{ current > 0 ? '↑' : '↓' }}</span>
          </div>
        </div>
        <div class="loading-overlay" v-if="!initialized">
          <div class="spinner"></div>
          <p>正在拉取物理引擎及 3D 模型...</p>
        </div>
      </div>

      <!-- 交互控制 -->
      <div class="viz-controls">
        <div class="control-header">
          <div class="title">安培定则分量调节</div>
          <div class="formula" v-latex> \$2\$ </div>
        </div>

        <div class="control-group glass">
          <div class="label-row">
            <label>导线电流强度 (I)</label>
            <span class="value">{{ current }}A</span>
          </div>
          <el-slider v-model="current" :min="-10" :max="10" :step="0.5" @input="updateCurrent"></el-slider>
        </div>

        <div class="theory-card">
           <i class="el-icon-info"></i>
           <p><strong>物理原理：</strong>电流流过导线时，周围会产生闭合磁感线。利用右手螺旋定则，拇指指向电流 <strong>(I)</strong>，则弯曲的四指方向即为磁感应强度 <strong>(B)</strong> 方向。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default { props: { knowledgePoint: String },
  name: 'AmpereLawViz',
  data() {
    return {
      current: 5,
      initialized: false,
      running: true
    };
  },
  mounted() {
    this.initTimer = setTimeout(() => {
      this.initThree();
    }, 300);
  },
  beforeDestroy() {
    this.running = false;
    clearTimeout(this.initTimer);
    window.removeEventListener('resize', this.handleResize);
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
      if (this.$refs.canvasContainer && this.renderer.domElement) {
        this.$refs.canvasContainer.removeChild(this.renderer.domElement);
      }
    }
  },
  methods: {
    initThree() {
      const container = this.$refs.canvasContainer;
      if (!container) return;

      const width = container.clientWidth;
      const height = container.clientHeight;

      if (width === 0 || height === 0) {
        this.initTimer = setTimeout(this.initThree, 500);
        return;
      }

      // Scene setup
      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(45, width / height, 1, 1000);
      this.camera.position.set(55, 40, 70);
      this.camera.lookAt(0, 0, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      container.appendChild(this.renderer.domElement);

      // Core Elements
      // 1. Central Wire
      const wireGeo = new THREE.CylinderGeometry(1.2, 1.2, 80, 20);
      const wireMat = new THREE.MeshStandardMaterial({ 
        color: 0x555555, 
        metalness: 0.8, 
        roughness: 0.2 
      });
      const wire = new THREE.Mesh(wireGeo, wireMat);
      this.scene.add(wire);

      // 2. Current Flow Arrows
      this.arrows = new THREE.Group();
      for (let i = -1; i <= 1; i++) {
        const arrow = new THREE.ArrowHelper(
          new THREE.Vector3(0, 1, 0),
          new THREE.Vector3(0, i * 20, 0),
          8, 0xff4757, 3, 1
        );
        this.arrows.add(arrow);
      }
      this.scene.add(this.arrows);

      // 3. Magnetic Rings
      this.rings = new THREE.Group();
      const ringCount = 4;
      for (let r = 1; r <= ringCount; r++) {
        const radius = r * 5 + 3;
        const ringGeo = new THREE.TorusGeometry(radius, 0.2, 16, 60);
        const ringMat = new THREE.MeshBasicMaterial({ 
          color: 0x00d2ff, 
          transparent: true, 
          opacity: 0.3 
        });
        const ring = new THREE.Mesh(ringGeo, ringMat);
        ring.rotation.x = Math.PI / 2;
        ring.position.y = 0;
        
        // Add directions to rings
        const tipGroup = new THREE.Group();
        for (let j = 0; j < 4; j++) {
           const angle = (j / 4) * Math.PI * 2;
           const tip = new THREE.Mesh(
             new THREE.ConeGeometry(0.8, 2, 8),
             new THREE.MeshBasicMaterial({ color: 0x00d2ff })
           );
           tip.position.set(Math.cos(angle) * radius, 0, Math.sin(angle) * radius);
           tip.rotation.y = -angle;
           tip.rotation.x = Math.PI / 2;
           tipGroup.add(tip);
        }
        ring.add(tipGroup);
        this.rings.add(ring);
      }
      this.scene.add(this.rings);

      // Lighting
      this.scene.add(new THREE.AmbientLight(0xffffff, 0.7));
      const light = new THREE.PointLight(0xffffff, 1, 200);
      light.position.set(20, 50, 20);
      this.scene.add(light);

      window.addEventListener('resize', this.handleResize);
      this.initialized = true;
      this.animate();
    },
    handleResize() {
      if (!this.renderer || !this.$refs.canvasContainer) return;
      const width = this.$refs.canvasContainer.clientWidth;
      const height = this.$refs.canvasContainer.clientHeight;
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
    },
    updateCurrent() {
      if (!this.arrows) return;
      const dir = this.current >= 0 ? 1 : -1;
      this.arrows.children.forEach(a => {
        a.setDirection(new THREE.Vector3(0, dir, 0));
        a.visible = Math.abs(this.current) > 0.1;
      });
    },
    animate() {
      if (!this.running) return;
      requestAnimationFrame(this.animate);
      
      const speed = this.current * 0.005;
      if (this.rings) {
        this.rings.children.forEach((ring, i) => {
          ring.rotation.z += speed / (i + 1);
        });
      }

      this.renderer.render(this.scene, this.camera);
    }
  }
};
</script>

<style scoped lang="scss">
.ampere-law-viz {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.viz-layout {
  flex: 1;
  display: flex;
  position: relative;
  min-height: 0;
  height: 100%;
}

.viz-stage-3d {
  flex: 1.8;
  position: relative;
  background: radial-gradient(circle at center, rgba(0, 50, 100, 0.1) 0%, transparent 100%);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  
  canvas {
    display: block;
  }
}

.viz-controls {
  width: 280px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  background: rgba(255, 255, 255, 0.02);
  border-left: 1px solid rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  overflow-y: auto;
}

.control-header {
  .title { font-size: 1.1rem; color: #fff; font-weight: 600; margin-bottom: 4px; }
  .formula { font-size: 0.85rem; color: #00d2ff; font-family: 'Times New Roman', serif; font-style: italic; }
}

.control-group {
  padding: 20px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
  .label-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 12px;
    label { font-size: 0.85rem; color: #888; }
    .value { font-size: 1rem; color: #00d2ff; font-family: monospace; }
  }
}

.theory-card {
  padding: 16px;
  background: rgba(0, 210, 255, 0.05);
  border-radius: 12px;
  border: 1px dashed rgba(0, 210, 255, 0.2);
  display: flex;
  gap: 12px;
  i { color: #00d2ff; margin-top: 3px; }
  p { margin: 0; font-size: 0.85rem; color: #aaa; line-height: 1.6; strong { color: #fff; } }
}

.hud-overlay {
  position: absolute;
  top: 20px;
  left: 20px;
  pointer-events: none;
  .status-badge {
    padding: 8px 16px;
    background: rgba(0, 0, 0, 0.4);
    border-radius: 8px;
    border-left: 3px solid #00d2ff;
    &.pos { border-left-color: #ff4757; }
    &.neg { border-left-color: #00d2ff; }
    label { display: block; font-size: 0.65rem; color: #666; text-transform: uppercase; letter-spacing: 1px; }
    span { font-size: 1rem; color: #fff; font-weight: bold; }
  }
}

.loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(2, 4, 10, 0.8);
  .spinner {
    width: 32px; height: 32px;
    border: 3px solid rgba(0, 210, 255, 0.1);
    border-top-color: #00d2ff;
    border-radius: 50%;
    animation: spin 1s infinite linear;
    margin-bottom: 16px;
  }
  p { color: #555; font-size: 0.9rem; }
}

@keyframes spin { to { transform: rotate(360deg); } }
</style>
